import type { Container, Sprite } from 'pixi.js';
import { makeSprite } from '@/assets/resolver';
import type { BulletSystem } from '@/entities/bullets';
import type { BossDef } from './boss-base';

/**
 * I1 Centipede — def, tuning and chain types
 * (class logic in centipede.ts). docs/02 + docs/12 I1.
 */
export const CENTIPEDE: BossDef = {
  id: 'centipede',
  nameKey: 'boss.centipede',
  tier: 2,
  minRoom: 4,
  baseHp: 1300,
  radius: 24, // head radius — custom collision overrides per node
  bodyKey: 'boss/centipede/head',
  movement: 'orbit', // unused — fully custom movement
  contactDamage: 1,
  phases: [{ hpPct: 1.0, patterns: [] }],
  evolve: [
    {
      // Magma burst: the lead head vents a ring of slag (from boss x/y)
      tier: 2,
      pattern: {
        type: 'radial', count: 6, interval: 3.8, spinDeg: 30,
        bullet: 'bullet/enemy/ring', speed: 140, radius: 10, damage: 2,
      },
    },
    {
      // Mandible snap: a fast aimed pair read from the head flash
      tier: 3,
      pattern: {
        type: 'aimed', count: 2, spreadDeg: 8, interval: 3.0, burst: 2, burstGap: 0.2,
        telegraph: 0.45,
        bullet: 'bullet/enemy/ball', speed: 300, radius: 10, damage: 1,
      },
    },
  ],
};

export const SEG_COUNT = 12; // body nodes behind the head
export const SEG_DIST = 31;
export const HEAD_RADIUS = 24;
export const SEG_RADIUS = 17;
export const HEAD_DMG_MUL = 0.45; // armored skull (docs/12 I1: head takes LESS)
export const BODY_DMG_MUL = 1.4; // soft cores (body takes MORE)
export const MID_HP_BONUS = 0.6; // middle nodes are beefier — splits are earned
export const SPEED = 235;
export const STEER = 3.0;
export const HEAD_FIRE_INTERVAL = 2.6;
export const SIDE_FIRE_INTERVAL = 3.4;
export const SHORT_SPEED_BONUS = 0.6; // up to +60% speed as a chain shrinks
// Regrowth (docs/12 L4): severed pieces slowly rebuild their tails
export const GROW_INTERVAL = 9;
export const GROW_HP_MUL = 0.8; // finite: see BOSS_MECHANICS.centipede

export interface Node {
  x: number;
  y: number;
  prevX: number;
  prevY: number;
  hp: number;
  sprite: Sprite;
  head: boolean;
}

/** Surface sweep → dig (tell) → underground → erupt (centipede-move.ts). */
export type ChainState = 'surface' | 'dig' | 'under' | 'erupt';

export interface Chain {
  nodes: Node[]; // nodes[0] is the head
  vx: number;
  vy: number;
  phase: number;
  dir: 1 | -1;
  state: ChainState;
  /** Time left in dig/erupt; time spent underground */
  stateT: number;
  burrowTimer: number;
  /** Eruption mound (locked on the ship's spot) */
  tx: number;
  ty: number;
  headFire: number;
  sideFire: number;
}

/** Initial head + segment chain; middle third carries bonus HP. */
export function buildInitialNodes(
  view: Container, x: number, y: number, maxHp: number,
): Node[] {
  // Normalize so the whole chain sums to EXACTLY maxHp (X2: no >100% bar)
  let weights = 0;
  for (let i = 0; i <= SEG_COUNT; i++) weights += isMid(i) ? 1 + MID_HP_BONUS : 1;
  const nodeHp = maxHp / weights;
  const nodes: Node[] = [];
  for (let i = 0; i <= SEG_COUNT; i++) {
    const head = i === 0;
    const sprite = makeSprite(head ? 'boss/centipede/head' : 'boss/centipede/segment');
    if (!head) sprite.scale.set(1 - (i / SEG_COUNT) * 0.25);
    view.addChild(sprite);
    const mid = isMid(i);
    nodes.push({
      x: x - i * SEG_DIST, y, prevX: x - i * SEG_DIST, prevY: y,
      hp: nodeHp * (mid ? 1 + MID_HP_BONUS : 1), sprite, head,
    });
  }
  return nodes;
}

/** Middle nodes are beefier — splits are earned. */
function isMid(i: number): boolean {
  return i > SEG_COUNT * 0.33 && i < SEG_COUNT * 0.75;
}

/** Severed pieces regrow one tail node (docs/12 L4). */
export function growTail(view: Container, c: Chain, hp: number): void {
  const tail = c.nodes[c.nodes.length - 1]!;
  const sprite = makeSprite('boss/centipede/segment');
  sprite.scale.set(0.75);
  sprite.position.set(tail.x, tail.y);
  view.addChild(sprite);
  c.nodes.push({ x: tail.x, y: tail.y, prevX: tail.x, prevY: tail.y, hp, sprite, head: false });
}

/**
 * Head twin shots + perpendicular side-turret volleys.
 * @param rate fire-rate multiplier (short-chain bonus × tier tempo)
 * @param shot bullet speed multiplier (tier ramp)
 */
export function fireChain(
  c: Chain, dt: number, rate: number, px: number, py: number,
  bullets: BulletSystem, shot: number,
): void {
  const head = c.nodes[0]!;
  c.headFire -= dt;
  if (c.headFire <= 0) {
    c.headFire = HEAD_FIRE_INTERVAL / rate;
    const base = Math.atan2(py - head.y, px - head.x);
    for (const off of HEAD_SPREAD) spawnShot(bullets, head.x, head.y, base + off, 265 * shot, 4);
  }
  // Side turrets: alternate segments volley perpendicular to travel
  c.sideFire -= dt;
  if (c.sideFire <= 0) {
    c.sideFire = SIDE_FIRE_INTERVAL / rate;
    for (let i = 2; i < c.nodes.length; i += 3) {
      const seg = c.nodes[i]!;
      const prev = c.nodes[i - 1]!;
      const a = Math.atan2(seg.y - prev.y, seg.x - prev.x) + Math.PI / 2;
      spawnShot(bullets, seg.x, seg.y, a, 140 * shot, 3.5, 9);
      spawnShot(bullets, seg.x, seg.y, a + Math.PI, 140 * shot, 3.5, 9);
    }
  }
}

const HEAD_SPREAD: ReadonlyArray<number> = [-0.16, 0.16];

function spawnShot(
  bullets: BulletSystem, x: number, y: number, a: number, speed: number, life: number,
  radius = 10,
): void {
  bullets.spawn({
    x, y, dirX: Math.cos(a), dirY: Math.sin(a), speed, life, radius, damage: 1,
    friendly: false, texKey: 'bullet/enemy/ball', rotates: false,
  });
}

/** Nearest living node inside a blast circle (X5) — no remote splits. */
export function nodeInBlast(
  chains: ReadonlyArray<Chain>, x: number, y: number, radius: number,
): Node | null {
  let best: Node | null = null;
  let bestD = Infinity;
  for (const c of chains) {
    if (c.state === 'under') continue; // nothing to hit underground
    for (const n of c.nodes) {
      if (n.hp <= 0) continue;
      const d = Math.hypot(n.x - x, n.y - y) - (n.head ? HEAD_RADIUS : SEG_RADIUS);
      if (d < radius && d < bestD) {
        bestD = d;
        best = n;
      }
    }
  }
  return best;
}

/** Auto-aim target: nearest soft body node; a head only when it is alone. */
export function nearestCore(chains: ReadonlyArray<Chain>, px: number, py: number): Node | null {
  let best: Node | null = null;
  let bestD = Infinity;
  for (const c of chains) {
    if (c.state === 'under') continue;
    for (const n of c.nodes) {
      if (n.head && c.nodes.length > 1) continue; // armored skull
      const d = (n.x - px) ** 2 + (n.y - py) ** 2;
      if (d < bestD) {
        bestD = d;
        best = n;
      }
    }
  }
  return best;
}
